import { z } from 'zod';
import { formatLocalTime } from 'shared-utils';
import { JobDatesCrewList } from '../types/JobDateCrewList';

export const AccidentTypeSchema = z.enum(['Injury', 'Near Miss', 'Venue Damage', 'Equipment Damage', 'Vehicle Accident or Damage', 'Incident', 'Other'])
type AccidentType = z.infer<typeof AccidentTypeSchema>

export const InjuryTypeSchema = z.enum(['Head', 'Neck', 'Chest', 'Abdomen', 'Back', 'Shoulder', 'Arm', 'Elbow', 'Forearm', 'Wrist', 'Hand', 'Fingers', 'Hip', 'Leg', 'Knee', 'Ankle', 'Foot', 'Toes', 'Eyes', 'Ears', 'Internal Organs'])
export type InjuryType = z.infer<typeof InjuryTypeSchema>

const InjurySchema = z.object({
  type: InjuryTypeSchema,
  details: z.string().min(1, "Details are required")
})


const WitnessSchema = z.object({
  crew_id: z.string().optional(),
  name: z.string().optional(),
  telephone: z.string().optional(),
  email: z.string().optional(),
}).refine((data) => {
  const hasCrew = !!data.crew_id;
  const hasName = !!data.name;
  const hasContact = !!data.telephone || !!data.email;

  return hasCrew || (hasName && hasContact);
}, {
  message: "Either select crew member, or provide name with telephone or email",
  path: ["crew_id"],
});

export type Witness = z.infer<typeof WitnessSchema>;

const VehicleSchema = z.object({
  vehicle_id: z.string().nullish(),
  vehicle_reg: z.string().nullish(),
  vehicle_owner_name: z.string().nullish(),
  vehicle_owner_telephone: z.string().nullish(),
  vehicle_owner_email: z.string().nullish(),
}).refine((data) => {
  const hasId = !!data.vehicle_id;
  const hasName = !!data.vehicle_owner_name;
  const hasContact = !!data.vehicle_owner_telephone || !!data.vehicle_owner_email;

  return hasId || (hasName && hasContact);
}, {
  message: "Either select job vehicle, or provide vehicle reg and owner details",
  path: ["vehicle_id"],
});

export const accidentFormSchema = z.object({
  state: z.boolean().default(false),
  job_id: z.number().nullish(),
  job_date_id: z.number().nullish(),
  date: z.date(),
  time: z.string().min(1, "Time is required"),
  type: AccidentTypeSchema,
  name: z.string().optional(),
  address: z.string().optional(),
  position: z.string().optional(),
  crew_id: z.string().optional(),
  date_of_birth: z.date().optional(),
  telephone: z.string().optional(),
  email: z.string().optional(),
  place: z.string().min(1, "Location is required"),
  crew_reported: z.number().optional(),
  reported_name: z.string().optional(),
  reported_address: z.string().optional(),
  reported_position: z.string().optional(),
  details: z.string().min(1, "Details are required"),
  immediate: z.string().optional(),
  underlying: z.string().optional(),
  action: z.string().optional(),
  riddor: z.boolean().default(false),
  pat: z.boolean().default(false),
  witnesses: z.array(WitnessSchema).min(1, "At least one witness is required"),
  damage_item: z.string().optional(),
  damage_serial: z.string().optional(),
  damage_telephone: z.string().optional(),
  damage_email: z.string().optional(),
  damage_name: z.string().optional(),
  injuries: z.array(InjurySchema).optional(),
  injury_severity: z.string().optional(),
  exposure: z.string().optional(),
  conciousness: z.string().optional(),
  outdoors: z.boolean().optional(),
  vehicles: z.array(VehicleSchema).nullish(),
})
  .refine(
    (data) => {
      if (data.type === "Injury") {
        return !!data.injury_severity?.trim();
      }
      return true;
    },
    {
      message: "Injury severity is required.",
      path: ["injury_severity"],
    }
  );

export const mapAccidentForFrontend = (values: JobDatesCrewList | null | undefined) => {
  return {
    time: values?.date_started_at ? formatLocalTime(values?.date_started_at, "HH:mm") : '',
    date: values?.date_started_at || undefined,
    name: '',
    address: '',
    position: '',
    telephone: '',
    email: '',
    place: values?.venue_name || '',
    reported_name: '',
    reported_address: '',
    reported_position: '',
    details: '',
    immediate: '',
    underlying: '',
    action: '',
    witnesses: [{}],
    damage_item: '',
    damage_serial: '',
    damage_telephone: '',
    damage_email: '',
    damage_name: '',
    injuries: [],
    injury_severity: '',
    exposure: '',
    conciousness: '',
    outdoors: undefined,
    vehicles: [],
  }
}

export type AccidentFormValues = z.infer<typeof accidentFormSchema>;


export const mapAccidentForBackend = (values: AccidentFormValues) => {
  return {
    ...values,
    job_id: values.job_id || 0,
    job_date_id: values.job_date_id || 0,
    crew_id: values?.crew_id && values?.crew_id !== 'Other' ? Number(values?.crew_id) : undefined,
    witnesses: values?.witnesses?.map(witness => ({
      ...witness,
      crew_id: witness?.crew_id !== 'Other' ? Number(witness?.crew_id) : undefined,
    })),
    riddor: values?.riddor || INJURY_SEVERITIES.find(sev => sev.name === values?.injury_severity)?.riddor || false,
    vehicles: values?.vehicles?.map(vehicle => ({
      ...vehicle,
      vehicle_id: vehicle?.vehicle_id !== 'Other' ? Number(vehicle?.vehicle_id) : undefined,
      reg: vehicle?.vehicle_reg,
      owner_name: vehicle?.vehicle_owner_name,
      owner_telephone: vehicle?.vehicle_owner_telephone,
      owner_email: vehicle?.vehicle_owner_email
    }))
  }
}


export const getSectionOneTitle = (type: string) => {
  switch (type) {
    case 'Injury':
      return 'Who was injured?';
    case 'Near Miss':
      return 'Who had a near miss?';
    case 'Equipment Damage':
    case 'Venue Damage':
      return 'What was damaged?';
    case 'Vehicle Accident or Damage':
      return 'Who was involved?';
    case 'Incident':
      return 'Who was involved in the incident?';
    case 'Other':
      return 'Accident information';
  }
}

export const getDamageSectionTitle = (type: AccidentType) => {
  switch (type) {
    case 'Equipment Damage':
      return 'equipment';
    case 'Venue Damage':
      return 'property';
    case 'Vehicle Accident or Damage':
      return 'vehicle';
  }
}

export const INJURY_TYPE_SECTIONS = [
  {
    title: 'Head and Neck',
    options: [
      {
        option: 'Head',
        details:
          'Include details like scalp injuries, concussions, skull fractures, or facial injuries (e.g., broken nose, jaw).',
      },
      {
        option: 'Neck',
        details:
          'Note any neck sprains, strains, or whiplash injuries.',
      },
    ],
  },
  {
    title: 'Torso',
    options: [
      {
        option: 'Chest',
        details:
          'Describe any rib fractures, chest contusions, or internal injuries to the lungs, heart, or other organs.',
      },
      {
        option: 'Abdomen',
        details:
          'Note any abdominal pain, bruising, or internal organ damage.',
      },
      {
        option: 'Back',
        details:
          'Include details about spinal injuries, slipped discs, or back strains and sprains.',
      },
    ],
  },
  {
    title: 'Upper Extremities',
    options: [
      {
        option: 'Shoulder',
        details:
          'Describe any shoulder dislocations, fractures, or rotator cuff injuries.',
      },
      {
        option: 'Arm',
        details:
          'Include fractures, dislocations, or soft tissue injuries like sprains or strains.',
      },
      {
        option: 'Elbow',
        details:
          'Describe any elbow fractures, dislocations, or tendon injuries.',
      },
      {
        option: 'Forearm',
        details:
          'Include fractures, sprains, or strains.',
      },
      {
        option: 'Wrist',
        details:
          'Note any wrist fractures, sprains, or dislocations.',
      },
      {
        option: 'Hand',
        details:
          'Describe any hand fractures, dislocations, lacerations, or tendon injuries.',
      },
      {
        option: 'Fingers',
        details:
          'Include fractures, dislocations, or crush injuries.',
      },
    ],
  },
  {
    title: 'Lower Extremities',
    options: [
      {
        option: 'Hip',
        details:
          'Describe any hip dislocations, fractures, or sprains.',
      },
      {
        option: 'Leg',
        details:
          'Include fractures, dislocations, or soft tissue injuries.',
      },
      {
        option: 'Knee',
        details:
          'Note any knee injuries like ACL or MCL tears, meniscus damage, or dislocations.',
      },
      {
        option: 'Ankle',
        details:
          'Describe any ankle sprains, fractures, or dislocations.',
      },
      {
        option: 'Foot',
        details:
          'Include foot fractures, dislocations, or sprains.',
      },
      {
        option: 'Toes',
        details:
          'Include fractures, dislocations, or crush injuries.',
      },
    ],
  },
  {
    title: 'Other',
    options: [
      {
        option: 'Eyes',
        details:
          'Note any eye injuries, including vision loss, burns, or foreign objects in the eye.',
      },
      {
        option: 'Ears',
        details:
          'Describe any ear injuries, including hearing loss or ruptured eardrums.',
      },
      {
        option: 'Internal Organs',
        details:
          'Indicate any injuries to internal organs like the lungs, kidneys, liver, or spleen.',
      },
    ],
  },
];

export const INJURY_SEVERITIES = [
  {
    name: "Superficial injury",
    description:
      "Minor cuts, no first aid required, carried on with work unaffected.",
    riddor: false
  },
  {
    name: "Minor Injury",
    description:
      "Bruises, sprains or strains, minor cuts — typically requiring basic first aid. Person can return to work.",
    riddor: false
  },
  {
    name: "Moderate / 3 day Injury",
    description:
      "Moderate injuries requiring more than basic first aid or any absence from work for up to 7 days. Includes minor burns.",
    riddor: false
  },
  {
    name: "Serious / 7 day injury",
    description:
      "Severe cuts or lacerations requiring hospital attention or stitches, or absence from work for 7+ days. Includes severe burns, broken bones, head injuries (e.g., concussion). RIDDOR reportable.",
    riddor: true
  },
  {
    name: "Critical or life-changing",
    description:
      "Permanent injuries such as amputation. Includes any injury defined as critical or life-changing under RIDDOR. RIDDOR reportable.",
    riddor: true
  },
  {
    name: "Death",
    description: "Death of casualty. RIDDOR reportable.",
    riddor: true
  },
  {
    name: "Injury to Member of Public",
    description:
      "Any injury caused to someone not in the company’s employment. RIDDOR reportable.",
    riddor: true
  },
];